import React from 'react';
import '../../styles/configurator.css';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Toggle } from '../../components/ui/toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getAvailableQuarters, getCurrentQuarter, isSkuAvailable } from '../../constants/quarterConfig';
import { getAvailableEncodingSchemes } from '../../utils/configUtilities.jsx';
import { getVisibleOptions, findCurrentMapping } from '../../utils/productFilters';
import { CapacityInput, NodeCountInput } from './StorageSizingInputs';
import { DirectorCountInput } from './DirectorCountInput';
import { calculateNetworkPorts } from '../../utils/networkPorts';
import { MlecToggle } from './MlecToggle';
import { FlashNodeModelToggle } from './FlashNodeModelToggle';
import { log } from '../../utils/devLog';

const BaselineConfigCard = ({
  config,
  updateConfig,
  batchUpdateConfig,
  productDb,
  hasSpecialAccess,
  hasDualCpuAccess = false, // CONF-210/259: Flash Node Model control
  theme,
  capabilities,
  metrics
}) => {

const { directorCountMin, veloSsdCapacities, hddCapacities, compressionRatios, networkOptions, networkProtocols, availableJbodSizes, jbodSizeLabels, productMappings, serviceOptions } = capabilities;

  // Calculate Available HDD options based on user access and quarter availability
  const filteredJbodSizes = availableJbodSizes.filter(size => {
    const mappings = productMappings?.hybridHdd?.[size] || [];
    return mappings.some(m => !m.availableFrom || isSkuAvailable(m.availableFrom, config.quarter));
  });

  const filteredHddMappings = getVisibleOptions(
    productMappings?.hybridHdd?.[config.jbodSize] || [],
    { hasSpecialAccess, quarter: config.quarter }
  );

  const currentHddMapping = findCurrentMapping(
    filteredHddMappings,
    config.hybridHddPartNumber,
    config.hybridHddCapacity
  );

  // Calculate Available Hybrid SSD options based on user access and quarter availability
  const filteredHybridSsdMappings = getVisibleOptions(
    productMappings.hybridSsd || [],
    { hasSpecialAccess, quarter: config.quarter }
  );

  const currentHybridSsdMapping = findCurrentMapping(
    filteredHybridSsdMappings,
    config.hybridSsdPartNumber,
    config.hybridVpodSsdCapacity
  );
  // Calculate Available AFA SSD options based on user access and quarter availability
  const filteredAfaSsdMappings = getVisibleOptions(
    productMappings.afaSsd || [],
    { hasSpecialAccess, quarter: config.quarter }
  );

  const currentAfaSsdMapping = findCurrentMapping(
    filteredAfaSsdMappings,
    config.afaSsdPartNumber,
    config.afaVpodSsdCapacity
  );

  const computeRawCapacityTB = (nodeCount, storageType) => {
    if (storageType === 'afa') {
      return nodeCount * productDb.ssdPerVpod * config.afaVpodSsdCapacity;
    }
    return (nodeCount * productDb.ssdPerVpod * config.hybridVpodSsdCapacity)
         + (nodeCount * config.jbodSize * config.hybridHddCapacity);
  };

  const afaUseableCapacityPB = ((metrics?.afaEffectiveCapacity || 0) / 1000).toFixed(2);
  const hybridUseableCapacityPB = ((metrics?.hybridEffectiveCapacity || 0) / 1000).toFixed(2);

  const handleToggleMode = (newMode) => {
    if (newMode === 'nodeCount') {
      updateConfig({ sizeByNodeCount: true });
    } else {
      batchUpdateConfig({
        sizeByNodeCount: false,
        hybridMinRawCapacity: computeRawCapacityTB(config.hybridNodeCount, 'hybrid'),
        afaMinRawCapacity: computeRawCapacityTB(config.afaNodeCount, 'afa'),
      });
    }
  };

  return (
    <>
      {/* First Card: Baseline System Configuration */}
      <Card>
        <CardHeader className={theme.cardHeader}>
          <div className="flex justify-between items-center">
            <CardTitle className={theme.cardTitle}>Base System Configuration</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="grid bg-black gap-6 p-6 text-white" style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
          {/* Column 1: Essential Configuration (always required) */}
          <div className="config-column-divider">
            <h3 className="section-header-underlined">Subscription Configuration</h3>
            {/* Delivery Quarter */}
            <div>
              <label className="control-label">Delivery Quarter</label>
              <Select
                value={config.quarter}
                onValueChange={(value) => updateConfig({ quarter: value })}
              >
                <SelectTrigger className="select-trigger">
                  <SelectValue placeholder="Select Quarter" />
                </SelectTrigger>
                <SelectContent className="select-content">
                  {hasSpecialAccess ? (
                    getAvailableQuarters().map(quarter => (
                      <SelectItem key={quarter} value={quarter}>
                        {quarter.replace('-', ' ')}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value={getCurrentQuarter()}>
                      {getCurrentQuarter().replace('-', ' ')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {/* Subscription Duration */}
              <div>
                <label className="control-label">Subscription Duration</label>
                <Select
                  value={(config.subscriptionMonths || 0).toString()}
                  onValueChange={(value) => updateConfig({ subscriptionMonths: parseInt(value, 10) })}
                >
                  <SelectTrigger className="select-trigger">
                    <SelectValue placeholder="Select Subscription Months" />
                  </SelectTrigger>
                  <SelectContent className="select-content">
                    {(capabilities.subscriptionDurations
                        ?? (hasSpecialAccess ? [12, 24, 36, 48, 60, 72, 84] : [36, 48, 60, 72, 84])
                     ).map(months => (
                      <SelectItem key={months} value={months.toString()}>
                        {months} months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Service Option */}
              <div>
                <label className="control-label">Service Option</label>
                <Select
                  value={config.serviceOption}
                  onValueChange={(value) => updateConfig({ serviceOption: value })}
                >
                  <SelectTrigger className="select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="select-content">
                    {serviceOptions.filter(option => !option.hidden || option.key === config.serviceOption).map(option => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Future Spacer column */}
          {/* <div></div> */}

          {/* Column 2: Network Configuration */}
          <div className="config-column">
            <h3 className="section-header-underlined">Network Configuration</h3>
            {/* Network Speed — CONF-93. Labels and ids both come from the
                registry; the component must not restate the option list. */}
            <div>
              <label className="control-label">Network Speed</label>
              <Select
                value={config.interfaceOption}
                onValueChange={(value) => updateConfig({ interfaceOption: value })}
              >
                <SelectTrigger className="select-trigger">
                  <SelectValue placeholder="100GbE (Broadcom)" />
                </SelectTrigger>
                <SelectContent className="select-content">
                  {networkOptions.map(option => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Network Protocol */}
            <div>
              <label className="control-label">Network Protocol</label>
              <Select
                value={config.networkProtocol}
                onValueChange={(value) => updateConfig({ networkProtocol: value })}
              >
                <SelectTrigger className="select-trigger">
                  <SelectValue placeholder="TCP" />
                </SelectTrigger>
                <SelectContent className="select-content">
                  {networkProtocols.map(protocol => (
                    <SelectItem key={protocol} value={protocol}>
                      {protocol}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Derived, not stored. */}
            <div className="mt-2 text-sm text-gray-400">
              Network Ports: {calculateNetworkPorts(config, capabilities).totalPorts}
            </div>
          </div>

          {/* Column 3: Director Node Configuration */}
          <div className="config-column">
            <h3 className="section-header-underlined">Director Nodes (D-Nodes)</h3>
            {hasSpecialAccess ? (
              <>
                {/* For special access users: Director Count and Director SSD */}
                <div>
                  <label className="control-label">Director Count</label>
                  <DirectorCountInput
                    value={config.veloCount}
                    min={directorCountMin}
                    onChange={(veloCount) => updateConfig({ veloCount })}
                  />
                </div>
                <div>
                  {/* Director NVMe SSD Selection 
                  <label className="control-label">Director NVMe SSD</label>
                  <Select
                    value={config.veloSsdCapacity.toString()}
                    onValueChange={(value) => updateConfig({ veloSsdCapacity: parseFloat(value) })}
                  >
                    <SelectTrigger className="select-trigger">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="select-content">
                      {veloSsdCapacities.map(size => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}TB
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                MORK TODO : Uncomment when this gets added back in - for now assume default 1.9TB*/}
                </div>
              </>
            ) : (
              <>
                {/* For regular users: Single Director Count selection */}
                <div>
                  <label className="control-label">Director Count</label>
                  <DirectorCountInput
                    value={config.veloCount}
                    min={directorCountMin}
                    onChange={(veloCount) => updateConfig({ veloCount })}
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Second Card: Performance, Capacity and Reliability Requirements */}
      <Card>
        <CardHeader className={theme.cardHeader}>
          <div className="flex items-center gap-4">
            <CardTitle className={theme.cardTitle}>Performance, Capacity and Reliability Requirements</CardTitle>
            <div className="flex rounded-md bg-black/30 p-0.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => handleToggleMode('capacity')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${!config.sizeByNodeCount ? theme.button : 'bg-black text-gray-300 hover:bg-gray-800'}`}
              >
                Raw Capacity
              </button>
              <button
                type="button"
                onClick={() => handleToggleMode('nodeCount')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${config.sizeByNodeCount ? theme.button : 'bg-black text-gray-300 hover:bg-gray-800'}`}
              >
                Node Count
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="bg-black text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-700">
            {/* AFA Storage Set Column - 1/3 width */}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold border-b pb-2 flex-1 min-w-0">NMVe Flash Storage Set (F-Nodes)</h3>
                <Toggle
                  leftLabel="Disabled"
                  rightLabel="Enabled"
                  isRightActive={config.afaEnabled}
                  labelClassName="text-white"
                  onToggle={() => {
                    // MORK TODO FIX to make sure we don't clear the F-Node config when disabling
                    // can I remove all this logic and just rely on the config logic rules to determine the opposite state
                    const newAfaValue = !(config.afaEnabled || false);
                    // If AFA is being disabled, ensure Hybrid is enabled
                    const newHybridValue = newAfaValue ? config.hybridEnabled : true;
                    // MORK TODO - Need to re-calculate the stats, metrics, and costs after toggling AFA
                    batchUpdateConfig({
                      afaEnabled: newAfaValue,
                      hybridEnabled: newHybridValue
                    });
                  }}
                  className='flex-row flex-shrink-0'
                />
              </div>

              <div className={`config-column ${config.afaEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                <div>
                  <label>
                    NVMe Flash {config.sizeByNodeCount ? 'Node Count' : 'Min RAW Capacity (PB)'}
                  </label>
                  {config.sizeByNodeCount
                    ? <NodeCountInput
                        nodeCount={config.afaNodeCount}
                        minNodes={3}
                        computedCapacityTB={computeRawCapacityTB(config.afaNodeCount, 'afa')}
                        useableCapacityPB={afaUseableCapacityPB}
                        onChange={(e) => updateConfig({ afaNodeCount: Math.max(3, parseInt(e.target.value) || 3) })}
                      />
                    : <CapacityInput
                        valuePb={config.afaMinRawCapacity === 0 ? '' : config.afaMinRawCapacity / 1000}
                        nodeCount={config.afaNodeCount}
                        useableCapacityPB={afaUseableCapacityPB}
                        onChange={(e) => {
                          const pbValue = parseFloat(e.target.value);
                          updateConfig({ afaMinRawCapacity: isNaN(pbValue) ? 0 : pbValue * 1000 });
                        }}
                        step="0.1"
                        min="0.3"
                      />
                  }
                </div>
                <div>
                  <label>Flash Storage Server NVMe SSD</label>
                  <Select
                    value={currentAfaSsdMapping?.partNumber || "VCH-NVME-7.6"}
                    onValueChange={(partNumber) => {
                      const mapping = filteredAfaSsdMappings.find(m => m.partNumber === partNumber);
                      if (mapping) {
                        batchUpdateConfig({
                          afaSsdPartNumber: partNumber,
                          afaVpodSsdCapacity: mapping.capacity
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="select-trigger">
                      <SelectValue placeholder="Select SSD" />
                    </SelectTrigger>
                    <SelectContent className="select-content">
                      {filteredAfaSsdMappings.map((mapping) => (
                        <SelectItem
                          key={mapping.partNumber}
                          value={mapping.partNumber}
                        >
                          {mapping.displayName}
                          {mapping.availableFrom && (
                            <span className="text-xs text-gray-400 ml-2">
                              (Available {mapping.availableFrom})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FlashNodeModelToggle config={config} updateConfig={updateConfig} hasDualCpuAccess={hasDualCpuAccess} />
                <div className="grid grid-cols-10 gap-2">
                  <div className="col-span-6">
                    <label>NVMe Flash Encoding Scheme</label>
                    <Select
                      value={config.afaEncodingScheme}
                      onValueChange={(value) => batchUpdateConfig({
                        afaEncodingScheme: value,
                        afaEncodingSchemeUserSelected: true // Mark as user-selected
                      })}
                    >
                      <SelectTrigger className="select-trigger">
                        <SelectValue>
                          {config.afaEncodingScheme}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        {getAvailableEncodingSchemes(config.afaNodeCount > 0 ? config.afaNodeCount : 3).map(scheme => (
                          <SelectItem
                            key={scheme}
                            value={scheme}
                          >
                            {scheme}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <label>Spares</label>
                    <Select
                      value={config.afaSpares?.toString() || "2"}
                      onValueChange={(value) => {
                        const sparesValue = parseInt(value);
                        log('Setting AFA spares to:', sparesValue); // Debug log
                        updateConfig({ afaSpares: sparesValue });
                      }}
                    >
                      <SelectTrigger className="select-trigger">
                        <SelectValue>
                          {config.afaSpares || 2}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        {config.availableSpares.map(spares => (
                          <SelectItem
                            key={spares}
                            value={spares.toString()}
                          >
                            {spares}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Empty column for spacing and Future Feature*/}
                  <div className="col-span-4"></div>
                </div>
                {/* F-Node Data Compressibility with End-to-End Encryption toggle */}
                <div>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Data Compressibility Dropdown - Left Side (66% - 2 columns) */}
                    <div className="col-span-2">
                      <label>Data Compressibility</label>
                      <Select
                        value={config.afaCompressionRatio?.toString() || "1.0"}
                        onValueChange={(value) => updateConfig({ afaCompressionRatio: parseFloat(value) })}
                        disabled={config.afaEndToEndEncryption || !config.afaEnabled}
                      >
                        <SelectTrigger className={`select-trigger ${(config.afaEndToEndEncryption || !config.afaEnabled) ? 'select-trigger-disabled' : ''}`}>
                          <SelectValue placeholder=" 1:1" />
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          {compressionRatios.map(size => (
                            <SelectItem key={size} value={size.toString()}> {"  "}{size}:1</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {config.afaEndToEndEncryption && (
                        <p className="text-xs text-gray-400 mt-1">Compression locked to 1:1 when E2E encryption is enabled</p>
                      )}
                    </div>
                    {/* End-to-End Encryption Toggle - Right Side (34% - 1 column) */}
                    <div className="col-span-1">
                      <label>End-to-End Encryption</label>
                      <Toggle
                        leftLabel="Off"
                        rightLabel="On"
                        isRightActive={config.afaEndToEndEncryption}
                        onToggle={() => {
                          const newEncryptionState = !config.afaEndToEndEncryption;
                          updateConfig({ afaEndToEndEncryption: newEncryptionState });
                        }}
                        disabled={!config.afaEnabled}
                        labelClassName="text-xs text-white"
                        className="h-10"
                      />
                      {config.afaEndToEndEncryption && (
                        <p className="text-xs text-green-400 mt-1">✓ E2E encryption active</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hybrid Storage Set Column - 2/3 width */}
            <div className="p-4 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold border-b pb-2 flex-1 min-w-0">Mixed Fleet Storage Set (S-Nodes)</h3>
                <Toggle
                  leftLabel="Disabled"
                  rightLabel="Enabled"
                  isRightActive={config.hybridEnabled}
                  labelClassName="text-white"
                  onToggle={() => {
                    // MORK TODO FIX to make sure we don't clear the S-Node config when disabling
                    // can I remove all this logic and just rely on the config logic rules to determine the opposite state
                    const newHybridValue = !(config.hybridEnabled || false);
                    // If Hybrid is being disabled, ensure AFA is enabled
                    const newAfaValue = newHybridValue ? config.afaEnabled : true;
                    // MORK TODO - Need to re-calculate the stats, metrics, and costs after toggling Hybrid
                    batchUpdateConfig({
                      hybridEnabled: newHybridValue,
                      afaEnabled: newAfaValue
                    });
                  }}
                  className='flex-row flex-shrink-0'
                />
              </div>

              <div className={`${config.hybridEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                <div className="grid grid-cols-2 gap-4">
                  {/* First Column */}
                  <div className="config-column">
                    <div>
                      <label>
                        Mixed Fleet {config.sizeByNodeCount ? 'Node Count' : 'Min RAW Capacity (PB)'}
                      </label>
                      {config.sizeByNodeCount
                        ? <NodeCountInput
                            nodeCount={config.hybridNodeCount}
                            minNodes={3}
                            computedCapacityTB={computeRawCapacityTB(config.hybridNodeCount, 'hybrid')}
                            useableCapacityPB={hybridUseableCapacityPB}
                            onChange={(e) => updateConfig({ hybridNodeCount: Math.max(3, parseInt(e.target.value) || 3) })}
                          />
                        : <CapacityInput
                            valuePb={config.hybridMinRawCapacity === 0 ? '' : config.hybridMinRawCapacity / 1000}
                            nodeCount={config.hybridNodeCount}
                            useableCapacityPB={hybridUseableCapacityPB}
                            onChange={(e) => {
                              const pbValue = parseFloat(e.target.value);
                              updateConfig({ hybridMinRawCapacity: isNaN(pbValue) ? 0 : pbValue * 1000 });
                            }}
                            step="1"
                            min="3"
                          />
                      }
                    </div>
                    <div>
                      <label>Mixed Fleet Storage Server NVMe SSD</label>
                      <Select
                        value={currentHybridSsdMapping?.partNumber || "VCH-NVME-3.8"}
                        onValueChange={(partNumber) => {
                          const mapping = filteredHybridSsdMappings.find(m => m.partNumber === partNumber);
                          if (mapping) {
                            batchUpdateConfig({
                              hybridSsdPartNumber: partNumber,
                              hybridVpodSsdCapacity: mapping.capacity
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="select-trigger">
                          <SelectValue placeholder="Select SSD Size" />
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          {filteredHybridSsdMappings.map((mapping) => (
                            <SelectItem
                              key={mapping.partNumber}
                              value={mapping.partNumber}
                            >
                              {mapping.displayName}
                              {mapping.availableFrom && (
                                <span className="text-xs text-gray-400 ml-2">
                                  (Available {mapping.availableFrom})
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* S-Node Data Compressibility with End-to-End Encryption toggle */}
                    <div>
                      <div className="grid grid-cols-3 gap-3">
                        {/* Data Compressibility Dropdown - Left Side (66% - 2 columns) */}
                        <div className="col-span-2">
                          <label>Data Compressibility</label>
                          <Select
                            value={config.hybridCompressionRatio?.toString() || "1.25"}
                            onValueChange={(value) => updateConfig({ hybridCompressionRatio: parseFloat(value) })}
                            disabled={config.hybridEndToEndEncryption || !config.hybridEnabled}
                          >
                            <SelectTrigger className={`select-trigger ${(config.hybridEndToEndEncryption || !config.hybridEnabled) ? 'select-trigger-disabled' : ''}`}>
                              <SelectValue placeholder=" 1.25:1" />
                            </SelectTrigger>
                            <SelectContent className="select-content">
                              {compressionRatios.map(size => (
                                <SelectItem key={size} value={size.toString()}>{"  "}{size}:1</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {config.hybridEndToEndEncryption && (
                            <p className="text-xs text-gray-400 mt-1">Compression locked to 1:1 when E2E encryption is enabled</p>
                          )}
                        </div>
                        {/* End-to-End Encryption Toggle - Right Side (34% - 1 column) */}
                        <div className="col-span-1">
                          <label>End-to-End Encryption</label>
                          <Toggle
                            leftLabel="Off"
                            rightLabel="On"
                            isRightActive={config.hybridEndToEndEncryption}
                            onToggle={() => {
                              const newEncryptionState = !config.hybridEndToEndEncryption;
                              updateConfig({ hybridEndToEndEncryption: newEncryptionState });
                            }}
                            disabled={!config.hybridEnabled}
                            labelClassName="text-xs text-white"
                            className="h-10"
                          />
                          {config.hybridEndToEndEncryption && (
                            <p className="text-xs text-green-400 mt-1">✓ E2E encryption active</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Second Column */}
                  <div className="config-column">
                    <div>
                      <label>Mixed Fleet JBOD Configuration</label>
                      <Select
                        value={config.jbodSize}
                        onValueChange={(value) => updateConfig({ jbodSize: Number(value) })}
                      >
                        <SelectTrigger className="select-trigger">
                          <SelectValue>
                            {jbodSizeLabels?.[config.jbodSize] ?? `${config.jbodSize} drives`}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          {filteredJbodSizes.map(size => (
                            <SelectItem key={size} value={size.toString()}>
                              {jbodSizeLabels?.[size] ?? `${size} drives`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label>HDD Capacity</label>
                      <Select
                        value={currentHddMapping?.partNumber || ''}
                        onValueChange={(partNumber) => {
                          const mapping = filteredHddMappings.find(m => m.partNumber === partNumber);
                          if (mapping) {
                            updateConfig({ 
                              hybridHddCapacity: mapping.capacity,
                              hybridHddPartNumber: partNumber
                            });
                          }
                        }} 
                      >
                        <SelectTrigger className="select-trigger">
                          <SelectValue placeholder="Select HDD Size" />
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          {filteredHddMappings.map(mapping => (
                            <SelectItem 
                              key={mapping.partNumber} 
                              value={mapping.partNumber}
                            >
                              {mapping.displayName}
                              {mapping.availableFrom && (
                                <span className="text-xs text-gray-400 ml-2">
                                  (Available {mapping.availableFrom})
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-10 gap-2">
                      <div className="col-span-5">
                        <label>Mixed Fleet Encoding Scheme</label>
                        <Select
                          value={config.hybridEncodingScheme}
                          onValueChange={(value) => batchUpdateConfig({
                            hybridEncodingScheme: value,
                            hybridEncodingSchemeUserSelected: true // Mark that user has made a selection
                          })}
                        >
                          <SelectTrigger className="select-trigger">
                            <SelectValue>
                              {config.hybridEncodingScheme}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="select-content">
                            {getAvailableEncodingSchemes(config.hybridNodeCount > 0 ? config.hybridNodeCount : 3).map(scheme => (
                              <SelectItem
                                key={scheme}
                                value={scheme}
                              >
                                {scheme}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <label>Spares</label>
                        <Select
                          value={config.hybridSpares?.toString() || "2"}
                          onValueChange={(value) => {
                            const sparesValue = parseInt(value);
                            log('Setting hybrid spares to:', sparesValue); // Debug log
                            updateConfig({ hybridSpares: sparesValue });
                          }}
                        >
                          <SelectTrigger className="select-trigger">
                            <SelectValue>
                              {config.hybridSpares || 2}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="select-content">
                            {config.availableSpares.map(spares => (
                              <SelectItem
                                key={spares}
                                value={spares.toString()}
                              >
                                {spares}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        {/* CONF-94: MLEC. Locked with a reason when the vendor or JBOD cannot
                            support it; R12 clears the flag if the JBOD changes underneath it. */}
                        <MlecToggle config={config} updateConfig={updateConfig} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default BaselineConfigCard;